import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { getApiUrl, getHeaders } from '../helpers/rest-utils';
import { catchError, map, tap } from 'rxjs/operators';
import { DataStorage } from '../data/data-storage';
import { PaginationInfo } from '../pagination';
import { Conversation, ConversationNote, EntityType, isLiveConversationState, normalizeCallState } from './conversation';
import { UserService } from '../user/user.service';
import { AnalyticsEvent, InternalAnalyticsService } from '../internal-analytics.service';

@Injectable({
  providedIn: 'root'
})

export class ConversationsService {
  activeFilter = CallStatus.All;
  /**
   * Multi-select Source filter (ENG-1812/ENG-1813). Each value is a
   * ConversationSource (call/sms/web/messenger/instagram). An empty array means
   * "All" — every channel, spam excluded — matching the API's absent-sources[]
   * default behavior.
   */
  selectedSources: string[] = [];

  // ENG-1812 / ENG-1813 — search + advanced filters
  searchTerm = '';
  /** Outcome-type filters (values from conversation.ts ConversationType: message/appointment/transferred/hangup). */
  typeFilters: string[] = [];
  /** Inclusive created-at range as yyyy-MM-dd strings. */
  dateFrom: string | null = null;
  dateTo: string | null = null;
  /** Transfer department ids (uuids) to filter "transferred to" (multi-select). */
  transferredTo: string[] = [];
  /** Active date-range preset key (last_hour | today | last_7_days | last_30_days); sent as `date_preset`. */
  activeDatePreset: string | null = null;

  /** Filter state persists across page reloads within the browser session. */
  private filterStorage: DataStorage = DataStorage.session();
  private readonly FILTER_STORAGE_KEY = 'conversation-inbox-filters';
  persistentStorage: DataStorage = DataStorage.persistent();
  list: BehaviorSubject<Conversation[]> = new BehaviorSubject([]);
  starredConversations: BehaviorSubject<Conversation[]> = new BehaviorSubject([]);
  sortedConversationList: Conversation[] = [];

  isLoading = false;
  meta: PaginationInfo = null;
  /** Monotonic token to discard stale loadMore responses after a filter change. */
  private loadToken = 0;

  constructor(public http: HttpClient, private user: UserService, private analytics: InternalAnalyticsService) {
    this.restoreFilters();
  }

  /** Persist the full filter state (folder, channel, search, advanced) for this session. */
  persistFilters(): void {
    this.filterStorage.set(this.FILTER_STORAGE_KEY, {
      activeFilter: this.activeFilter,
      selectedSources: this.selectedSources,
      searchTerm: this.searchTerm,
      typeFilters: this.typeFilters,
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      transferredTo: this.transferredTo,
      activeDatePreset: this.activeDatePreset,
    });
  }

  private restoreFilters(): void {
    const saved = this.filterStorage.get(this.FILTER_STORAGE_KEY);
    if (!saved) {
      return;
    }
    this.activeFilter = saved.activeFilter ?? CallStatus.All;
    this.selectedSources = Array.isArray(saved.selectedSources)
      ? saved.selectedSources.filter((s: string) => CONVERSATION_SOURCES.includes(s as ConversationSource))
      : [];
    this.searchTerm = saved.searchTerm ?? '';
    this.typeFilters = Array.isArray(saved.typeFilters) ? saved.typeFilters : [];
    this.dateFrom = saved.dateFrom ?? null;
    this.dateTo = saved.dateTo ?? null;
    this.transferredTo = Array.isArray(saved.transferredTo) ? saved.transferredTo : [];
    this.activeDatePreset = this.normalizeDatePreset(saved.activeDatePreset);
  }

  /**
   * Map any persisted preset key to the current API `date_preset` values,
   * migrating the pre-timezone keys (last7Days/last30Days) and dropping anything
   * unrecognized so a stale session can't send an invalid preset.
   */
  private normalizeDatePreset(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const legacy: Record<string, string> = { last7Days: 'last_7_days', last30Days: 'last_30_days' };
    const key = legacy[value] ?? value;
    const valid = ['last_hour', 'today', 'last_7_days', 'last_30_days'];
    return valid.includes(key) ? key : null;
  }

  /**
   * SMS feature enabled status (set by resolver based on SMS capabilities endpoint).
   * If status is not 'inactive', SMS is considered enabled.
   */
  private _smsFeatureEnabled = false;

  get hasSmsFeatureEnabled(): boolean {
    return this._smsFeatureEnabled;
  }

  set hasSmsFeatureEnabled(enabled: boolean) {
    this._smsFeatureEnabled = enabled;
  }

  /**
   * Build the Source (`sources[]`) query params for the conversations endpoint.
   * Empty selection means "All" — no params, so the API returns every channel
   * (spam excluded). Website Chat exists on every plan, so we never force
   * calls-only; the API resolves whatever sources are selected.
   */
  private getSourceParams(): string {
    return this.selectedSources
      .map((source) => `&sources[]=${encodeURIComponent(source)}`)
      .join('');
  }

  get isStarredFilterActive(): boolean {
    return this.activeFilter === CallStatus.Starred;
  }

  get isArchivedFilterActive(): boolean {
    return this.activeFilter === CallStatus.Archived;
  }

  get isSpamFilterActive(): boolean {
    return this.activeFilter === CallStatus.Spam;
  }

  get isUnviewedFilterActive(): boolean {
    return this.activeFilter === CallStatus.Open;
  }

  get hasSearchTerm(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  get hasDateRange(): boolean {
    return !!this.dateFrom || !!this.dateTo || !!this.activeDatePreset;
  }

  get hasAdvancedFilters(): boolean {
    return this.typeFilters.length > 0 || this.hasDateRange || this.transferredTo.length > 0;
  }

  /** Number of active advanced-filter dimensions, for the Filters button badge. */
  get advancedFilterCount(): number {
    return this.typeFilters.length + (this.hasDateRange ? 1 : 0) + this.transferredTo.length;
  }

  clearAdvancedFilters(): void {
    this.typeFilters = [];
    this.dateFrom = null;
    this.dateTo = null;
    this.transferredTo = [];
    this.activeDatePreset = null;
  }

  /** A non-empty Source selection means the inbox is narrowed below "All". */
  get hasSourceFilter(): boolean {
    return this.selectedSources.length > 0;
  }

  isSourceSelected(source: string): boolean {
    return this.selectedSources.includes(source);
  }

  /** Toggle one source. Tapping the last selected source returns to "All". */
  toggleSource(source: string): void {
    this.selectedSources = this.selectedSources.includes(source)
      ? this.selectedSources.filter((s) => s !== source)
      : [...this.selectedSources, source];
  }

  /** Clear all sources, returning to the "All" default. */
  clearSources(): void {
    this.selectedSources = [];
  }

  /** Build the search + advanced filter query string for the conversations endpoint. */
  private buildSearchFilterParams(): string {
    let params = '';

    if (this.hasSearchTerm) {
      params += `&q=${encodeURIComponent(this.searchTerm.trim())}`;
    }
    this.typeFilters.forEach((type) => {
      params += `&types[]=${encodeURIComponent(type)}`;
    });
    // A preset is resolved server-side (in the business timezone); a custom
    // range sends explicit from/to. They are mutually exclusive.
    if (this.activeDatePreset) {
      params += `&date_preset=${encodeURIComponent(this.activeDatePreset)}`;
    } else {
      if (this.dateFrom) {
        params += `&date_from=${this.dateFrom}`;
      }
      if (this.dateTo) {
        params += `&date_to=${this.dateTo}`;
      }
    }
    this.transferredTo.forEach((deptId) => {
      params += `&transferred_to[]=${encodeURIComponent(deptId)}`;
    });

    return params;
  }

  getTransferDepartments(): Observable<TransferDepartmentOption[]> {
    const businessId = this.user.activeBusiness?.id;
    return this.http.get<{ data?: RawTransferDepartment[] } | RawTransferDepartment[]>(
      `${getApiUrl()}businesses/${businessId}/transfer-departments`,
      { headers: { 'X-Business-ID': businessId } })
      .pipe(
        map((response) => {
          const list = Array.isArray(response) ? response : (response?.data ?? []);
          return list
            .map((d) => ({ id: String(d.id), department: d.department }))
            .filter((d) => !!d.department);
        }),
        catchError(() => of([] as TransferDepartmentOption[]))
      );
  }

  hasMore(): boolean {
    if (!this.meta) {
      return false;
    }
    return this.meta.currentPage < this.meta.lastPage;
  }

  /**
   * Fetch the next page and append it. A no-op while a load is in flight or
   * when the last page is already shown, so callers can invoke it freely from
   * scroll events, the fill check and the explicit "load older" fallback.
   */
  loadMore(): Observable<Conversation[]> {
    if (this.isLoading || !this.hasMore()) {
      return of(this.list.getValue());
    }
    return this.getAll(this.meta.currentPage + 1);
  }

  /**
   * Re-read page 1 for the same folder/filter/search without dropping the older
   * pages the user already scrolled through. Background refreshes (realtime
   * catch-up, the 10-minute poll, a business refresh) used to collapse the list
   * back to 50 rows and throw the user to the top of it (ENG-2407).
   */
  refreshFirstPage(): Observable<Conversation[]> {
    return this.getAll(1, true);
  }

  getAll(page = 1, keepLoadedPages = false): Observable<Conversation[]> {
    const loadedList = this.list.getValue() || [];
    const loadedPage = this.meta?.currentPage ?? 1;
    this.isLoading = true;
    // A page-1 load (filter change / refresh) supersedes any in-flight loadMore.
    // Bump the token so a stale page>1 response can't append onto the new list.
    if (page === 1) {
      this.loadToken++;
      this.persistFilters();
    }
    const requestToken = this.loadToken;
    // For Open (unviewed) filter, also include active conversations regardless of viewed status
    const statusFilter = this.isStarredFilterActive ? '&flagged=1' : this.isArchivedFilterActive ? '&archived=1' : this.isSpamFilterActive ? '&spam=1' : this.isUnviewedFilterActive ? '&viewed=0&active=1' : '';
    const sourceFilter = this.getSourceParams();
    const searchFilterParams = this.buildSearchFilterParams();

    return this.http.get(`${getApiUrl()}conversations?page=${page}&perPage=50${statusFilter}${sourceFilter}${searchFilterParams}`,
      { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(
        // A failed page must not leave the list stuck "loading" — that would
        // block every later loadMore().
        catchError((error) => {
          this.isLoading = false;
          return throwError(() => error);
        }),
        map((response: ConversationsResponse) => {
        // Drop a superseded loadMore: its filter context was replaced by a newer
        // page-1 load while it was in flight, so appending it would mix results.
        if (page > 1 && requestToken !== this.loadToken) {
          this.isLoading = false;
          return this.list.getValue();
        }
        const newCalls = response.data;
        newCalls.forEach((newLead) => {
          if (newLead.customer) {
            newLead.customer.phoneNumber = newLead.customer.phoneNumbers?.find((phoneNumber) => phoneNumber.isPrimary)?.number;
          }
          // Normalize chat status (AGENT_HANDLING/TAKEOVER_PENDING -> ACTIVE)
          if (newLead.status) {
            newLead.status = normalizeCallState(newLead.status as string);
          }
        });

        // Add mocked calls on first page for testing
        // if (page === 1) {ConversationState
        //   newCalls.unshift(...this.getMockedConversations());
        // }

        let calls = [];
        if (page !== this.meta?.currentPage && page > 1) {
          // New conversations push rows down a page, so the next page can
          // repeat rows that are already shown.
          const shownIds = new Set(this.list.getValue().map((call) => call.id));
          calls = [...this.list.getValue(), ...newCalls.filter((call) => !shownIds.has(call.id))];
        } else if (keepLoadedPages && page === 1 && loadedPage > 1) {
          calls = this.mergeFirstPage(newCalls, loadedList, response.meta);
        } else {
          calls = newCalls;
        }
        this.resetConversationList(calls);
        this.list.next(calls);
        const meta = response.meta;
        const filteredCounts = meta.filteredConversationsCount;
        meta.filteredCallsCount = {
          open: filteredCounts?.notViewed ?? 0,
          starred: filteredCounts?.flagged ?? 0,
          archived: filteredCounts?.archived ?? 0,
          spam: filteredCounts?.spam ?? 0,
          all: filteredCounts?.all ?? 0
        };
        // Conversation type counts from BE response
        meta.conversationTypeCounts = {
          calls: filteredCounts?.calls ?? 0,
          sms: filteredCounts?.sms ?? 0,
          web: filteredCounts?.web ?? 0,
          messenger: filteredCounts?.messenger ?? 0,
          instagram: filteredCounts?.instagram ?? 0,
          yelp: filteredCounts?.yelp ?? 0,
          all: filteredCounts?.all ?? 0
        };
        if (keepLoadedPages && page === 1 && loadedPage > 1 && calls.length > newCalls.length) {
          // The older pages are still on screen, so paging continues after them.
          meta.currentPage = Math.min(loadedPage, meta.lastPage);
        }
        this.setMeta(meta);
        this.isLoading = false;
        return calls;
      })
      ) as Observable<Conversation[]>;
  }

  getConversationDetails(id: string, entityType?: EntityType): Observable<Conversation> {
    if (!id) {
      return of(null);
    }

    // If entityType is not provided, try calls first, then chats as fallback
    if (!entityType) {
      return this.http.get<Conversation>(`${getApiUrl()}calls/${id}`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
        .pipe(
          catchError(() => {
            // If calls/{id} fails (404), try chats/{id}
            return this.http.get<Conversation>(`${getApiUrl()}chats/${id}`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
          }),
          map((response: any) => {
            const call = response.data;
            if (call?.customer) {
              call.customer.phoneNumber = call.customer?.phoneNumbers?.find((phoneNumber) => phoneNumber.isPrimary)?.number;
            }
            // Normalize chat status
            if (call?.status) {
              call.status = normalizeCallState(call.status);
            }
            return call;
          })
        );
    }

    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.get<Conversation>(`${getApiUrl()}${endpoint}/${id}`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => {
        const call = response.data;
        if (call?.customer) {
          call.customer.phoneNumber = call.customer?.phoneNumbers?.find((phoneNumber) => phoneNumber.isPrimary)?.number;
        }
        // Normalize chat status
        if (call?.status) {
          call.status = normalizeCallState(call.status);
        }
        return call;
      }));
  }

  updateConversationInList(updatedConversation: Conversation): void {
    const currentList = this.list.getValue();
    const index = currentList.findIndex(c => c.id === updatedConversation.id);
    if (index !== -1) {
      const updatedList = [...currentList];
      updatedList[index] = updatedConversation;
      this.list.next(updatedList);
    }
  }

  reset(): void {
    this.list.next([]);
  }

  view(id: string, entityType: EntityType, viewed: boolean = true): Observable<any> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.put(`${getApiUrl()}${endpoint}/${id}`, { viewed }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map(() => {
        const selectedCall = this.sortedConversationList?.find(lead => lead?.id === id);
        if (selectedCall) {
          selectedCall.viewed = viewed;
        }
        this.resetConversationList();
      }));
  }

  star(id: string, entityType: EntityType, flagged: boolean = true): Observable<any> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.put(`${getApiUrl()}${endpoint}/${id}`, { flagged }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
  }

  handleConversationArchive(id: string, entityType: EntityType): Observable<any> {
    return this.isArchivedFilterActive ?
      this.restore(id, entityType).pipe(tap(() => {
        this.meta.filteredCallsCount.archived = this.meta.filteredCallsCount.archived - 1
        this.meta.filteredCallsCount.all = this.meta.filteredCallsCount.all + 1;
      })) :
      this.archive(id, entityType).pipe(tap(() => {
        this.meta.filteredCallsCount.archived = this.meta.filteredCallsCount.archived + 1
        this.meta.filteredCallsCount.all = this.meta.filteredCallsCount.all - 1
      }));
  }

  archive(id: string, entityType: EntityType): Observable<any> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.delete(`${getApiUrl()}${endpoint}/${id}`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  restore(id: string, entityType: EntityType): Observable<any> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.patch(`${getApiUrl()}${endpoint}/${id}/restore`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
  }

  markAsSpam(id: string, entityType: EntityType, blockNumber: boolean = true): Observable<Conversation> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.post(`${getApiUrl()}${endpoint}/${id}/mark-spam`, { blockNumber }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => {
        const conversation = response.data;
        const selectedConversation = this.sortedConversationList?.find(c => c?.id === id);
        if (selectedConversation) {
          selectedConversation.type = conversation.type;
        }
        return conversation;
      }));
  }

  unmarkAsSpam(id: string, entityType: EntityType, unblockNumber: boolean = true): Observable<Conversation> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.post(`${getApiUrl()}${endpoint}/${id}/unmark-spam`, { unblockNumber }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => {
        const conversation = response.data;
        const selectedConversation = this.sortedConversationList?.find(c => c?.id === id);
        if (selectedConversation) {
          selectedConversation.type = conversation.type;
        }
        return conversation;
      }));
  }

  rateCall(id: string, entityType: EntityType, rating: number, ratingReason: string | null = null): Observable<any> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.post(`${getApiUrl()}${endpoint}/${id}/rating`, { rating, ratingReason }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => {
        const call = response.data;
        // Update the call in the list with the new rating
        const selectedCall = this.sortedConversationList?.find(c => c?.id === id);
        if (selectedCall) {
          selectedCall.rating = call.rating;
          selectedCall.ratingReason = call.ratingReason;
        }
        return call;
      }));
  }

  getNotes(id: string, entityType: EntityType): Observable<ConversationNote[]> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.get(`${getApiUrl()}${endpoint}/${id}/notes?source=user`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.data || []));
  }

  createNote(id: string, entityType: EntityType, note: string): Observable<ConversationNote> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.post(`${getApiUrl()}${endpoint}/${id}/notes`, { note, businessId: this.user.activeBusiness?.id, source: 'user' }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => {
        const createdNote = response.data;
        const conversation = this.sortedConversationList?.find(c => c?.id === id);
        if (conversation) {
          if (!conversation.notes) {
            conversation.notes = [];
          }
          conversation.notes.push(createdNote);
        }
        return createdNote;
      }));
  }

  updateNote(id: string, entityType: EntityType, noteId: string, note: string): Observable<ConversationNote> {
    const endpoint = entityType === EntityType.Chat ? 'chats' : 'calls';
    return this.http.put(`${getApiUrl()}${endpoint}/${id}/notes/${noteId}`, { note }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => {
        const updatedNote = response.data;
        const conversation = this.sortedConversationList?.find(c => c?.id === id);
        if (conversation?.notes) {
          const noteIndex = conversation.notes.findIndex(n => n.id === noteId);
          if (noteIndex !== -1) {
            conversation.notes[noteIndex] = updatedNote;
          }
        }
        return updatedNote;
      }));
  }

  /**
   * Fresh page 1 followed by the already-loaded rows that are older than it.
   * Loaded rows inside page 1's time range that the API no longer returns were
   * archived, spammed or filtered out elsewhere, so they are dropped.
   */
  private mergeFirstPage(firstPage: Conversation[], loaded: Conversation[], meta: PaginationInfo): Conversation[] {
    if (!firstPage.length || !(meta?.currentPage < meta?.lastPage)) {
      return firstPage;
    }

    const firstPageIds = new Set(firstPage.map((call) => call.id));
    const oldestOnFirstPage = Math.min(...firstPage.map((call) => new Date(call.startedAt).getTime()));
    const olderRows = loaded.filter((call) =>
      !firstPageIds.has(call.id) && new Date(call.startedAt).getTime() < oldestOnFirstPage && this.matchesActiveFolder(call)
    );

    return [...firstPage, ...olderRows];
  }

  /**
   * Kept rows can change after they were loaded (read, unstarred), so they must
   * still pass the folder's filter. Unread mirrors the API: viewed=0 or live.
   */
  private matchesActiveFolder(call: Conversation): boolean {
    if (this.isUnviewedFilterActive) {
      return !call.viewed || isLiveConversationState(call.status);
    }
    if (this.isStarredFilterActive) {
      return !!call.flagged;
    }
    return true;
  }

  setMeta(meta: PaginationInfo) {
    this.meta = meta;
  }

  getMeta(): PaginationInfo {
    return this.meta;
  }

  clearRecordings(): Observable<any> {
    this.analytics.trackEvent(AnalyticsEvent.ClearRecordings);
    return this.http.delete(`${getApiUrl()}recordings`, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
  }

  metaTakeover(id: string): Observable<Conversation> {
    return this.http.post(`${getApiUrl()}chats/${id}/meta-takeover`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.chat));
  }

  metaHandBack(id: string): Observable<any> {
    return this.http.post(`${getApiUrl()}chats/${id}/meta-hand-back`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  metaSendMessage(id: string, content: string): Observable<any> {
    return this.http.post(`${getApiUrl()}chats/${id}/meta-messages`, { content }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.message));
  }

  webTakeover(id: string): Observable<Conversation> {
    return this.http.post(`${getApiUrl()}chats/${id}/web-takeover`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.chat));
  }

  webHandBack(id: string): Observable<any> {
    return this.http.post(`${getApiUrl()}chats/${id}/web-hand-back`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  yelpTakeover(id: string): Observable<Conversation> {
    return this.http.post(`${getApiUrl()}chats/${id}/yelp-takeover`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.chat));
  }

  yelpHandBack(id: string): Observable<any> {
    return this.http.post(`${getApiUrl()}chats/${id}/yelp-hand-back`, {}, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } });
  }

  webSendMessage(id: string, content: string): Observable<any> {
    return this.http.post(`${getApiUrl()}chats/${id}/web-messages`, { content }, { headers: { 'X-Business-ID': this.user.activeBusiness?.id } })
      .pipe(map((response: any) => response.message));
  }

  resetConversationList(conversations?: Conversation[]): void {
    const conversationList = conversations || this.list.getValue();
    this.sortedConversationList = conversationList?.sort((a, b) => {
      return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
    });
  }

  private getMockedConversations(): Conversation[] {
    const now = Date.now();
    return [
      // Active SMS conversation (still in progress - shows details unlike calls)
      {
        id: `mock-sms-active`,
        businessId: 1,
        customerId: 1,
        bookingId: null,
        transcript: '',
        summary: 'Active conversation with potential customer about kennel services.',
        events: [],
        type: 'message' as any,
        entityType: EntityType.Chat,
        source: 'webchat' as any,
        customer: {
          id: 1,
          businessId: 1,
          firstName: 'Alexia',
          lastName: 'Power',
          isLead: true,
          additionalData: { height: '' },
          phoneNumbers: [{ number: '+1 (970) 819-0482', isPrimary: true } as any],
          phoneNumber: '+1 (970) 819-0482'
        },
        viewed: false,
        flagged: false,
        startedAt: new Date(now - 300000).toISOString(), // 5 minutes ago
        endedAt: null,
        recordingUrl: '',
        hasRecordingExpired: false,
        status: 'IN_PROGRESS' as any,
        bookingMessage: false,
        sentLinks: [],
        transferDestination: null,
        appointment: null,
        questions: [],
        turns: [
          { role: 'agent', content: 'Thanks for reaching out to Booth and Tumble Dog Boarding. How can I help you?', timestamp: new Date(now - 300000).toISOString() },
          { role: 'user', content: 'Hi, I need boarding for my dog this December for a week, can I come see what the kennels look like?', timestamp: new Date(now - 280000).toISOString() },
          { role: 'agent', content: 'Of course, I can help you schedule a viewing. We have some times available next week on:\n- Tuesday at 10:00 AM\n- Wednesday at 9:00 AM\n- Thursday at 2:00 PM', timestamp: new Date(now - 260000).toISOString() },
          { role: 'user', content: 'Wednesday works for me.', timestamp: new Date(now - 240000).toISOString() }
        ]
      },
      // SMS conversation - Take a message (completed)
      {
        id: `mock-sms-1`,
        businessId: 1,
        customerId: 2,
        bookingId: null,
        transcript: '',
        summary: 'Customer inquired about home renovation services and requested a quote.',
        events: [],
        type: 'message' as any,
        entityType: EntityType.Chat,
        source: 'webchat' as any,
        customer: {
          id: 2,
          businessId: 1,
          firstName: 'Sarah',
          lastName: 'Johnson',
          isLead: true,
          additionalData: { height: '' },
          phoneNumbers: [{ number: '+1 (555) 234-5678', isPrimary: true } as any],
          phoneNumber: '+1 (555) 234-5678'
        },
        viewed: false,
        flagged: false,
        startedAt: new Date(now - 3600000).toISOString(), // 1 hour ago
        endedAt: new Date(now - 3500000).toISOString(),
        recordingUrl: '',
        hasRecordingExpired: false,
        status: 'FINISHED' as any,
        bookingMessage: false,
        sentLinks: [],
        transferDestination: null,
        appointment: null,
        questions: [],
        turns: [
          { role: 'agent', content: 'Hello! Thank you for reaching out. I\'d be happy to help. What specific service are you interested in?', timestamp: new Date(now - 3600000).toISOString() },
          { role: 'user', content: 'Hi, I saw your ad and wanted to learn more about your services.', timestamp: new Date(now - 3580000).toISOString() },
          { role: 'agent', content: 'We offer a variety of home renovation services. Can you tell me more about your project?', timestamp: new Date(now - 3560000).toISOString() },
          { role: 'user', content: 'I need a quote for a home renovation project.', timestamp: new Date(now - 3540000).toISOString() },
          { role: 'agent', content: 'I\'ll have someone get back to you shortly with a quote. Can you provide a few details about the project?', timestamp: new Date(now - 3520000).toISOString() }
        ]
      },
      // Call - Appointment booked
      {
        id: `mock-call-1`,
        businessId: 1,
        customerId: 2,
        bookingId: null,
        transcript: `AI: Hello, thank you for calling. How can I help you today?
User: Hi, I would like to schedule an appointment.
AI: Of course! I can help you with that. What day works best for you?
User: How about tomorrow at 2pm?
AI: Let me check availability. Yes, tomorrow at 2pm is available. I'll book that for you.
User: Great, thank you!
AI: You're welcome! Can I get your email address to send a confirmation?
User: Sure, it's john.doe@example.com
AI: Perfect, I've got that. And may I ask what the appointment is regarding?
User: I need a consultation for a kitchen remodel.
AI: Wonderful! Kitchen remodels are one of our specialties. Do you have a rough idea of the scope?
User: Yes, I want to replace the countertops, cabinets, and maybe add an island.
AI: That sounds like a fantastic project. Our team will be able to discuss all those options with you.
User: How long does a typical kitchen remodel take?
AI: Depending on the scope, it can range from 4 to 8 weeks. The consultation will give you a more accurate timeline.
User: That's helpful to know. What should I bring to the appointment?
AI: If you have any inspiration photos or measurements, those are always helpful. But not required!
User: I have some Pinterest boards I can share.
AI: Perfect! Our designer loves seeing those. It really helps understand your style preferences.
User: Do you offer financing options?
AI: Yes, we do! We partner with several financing companies to offer flexible payment plans.
User: That's great. What's the typical down payment?
AI: Usually around 20-30%, but our team can discuss specific options during your consultation.
User: And the consultation is free, right?
AI: Absolutely! The initial consultation is completely free with no obligation.
User: Perfect. Is there parking available at your location?
AI: Yes, we have a free parking lot right in front of the building.
User: Great. One more question - can my spouse join the consultation virtually?
AI: Of course! We can set up a video call so they can participate remotely.
User: Excellent, that would be very helpful. I think that's all my questions for now.
AI: Wonderful! I've booked your appointment for tomorrow at 2pm. You'll receive a confirmation email shortly.
User: Thank you so much for all your help!
AI: My pleasure! We look forward to meeting you tomorrow. Have a great day!`,
        summary: 'Caller requested an appointment scheduling. Successfully booked for tomorrow at 2pm.',
        events: [],
        type: 'appointment' as any,
        entityType: EntityType.Call,
        customer: {
          id: 2,
          businessId: 1,
          firstName: 'John',
          lastName: 'Doe',
          isLead: true,
          additionalData: { height: '' },
          phoneNumbers: [{ number: '+1 (555) 123-4567', isPrimary: true } as any],
          phoneNumber: '+1 (555) 123-4567'
        },
        viewed: true,
        flagged: false,
        startedAt: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
        endedAt: new Date(Date.now() - 7020000).toISOString(),
        recordingUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
        hasRecordingExpired: false,
        status: 'FINISHED' as any,
        bookingMessage: false,
        sentLinks: [],
        transferDestination: null,
        appointment: {
          date: new Date(Date.now() + 86400000).toISOString(),
          description: 'Appointment with John Doe',
          url: 'https://calendar.google.com/calendar/event?eid=abc123xyz',
          reference: 'google_calendar',
          attendees: ['john.doe@example.com'],
          timezone: 'America/New_York'
        },
        questions: []
      },
      // Call - Transferred
      {
        id: `mock-call-2`,
        businessId: 1,
        customerId: 3,
        bookingId: null,
        transcript: 'AI: Hello, thank you for calling. How can I help you today?\nUser: I need to speak to someone about a billing issue.\nAI: I understand. Let me transfer you to our billing department.',
        summary: 'Customer had a billing question and was transferred to billing department.',
        events: [],
        type: 'transferred' as any,
        entityType: EntityType.Call,
        customer: {
          id: 3,
          businessId: 1,
          firstName: 'Mike',
          lastName: 'Smith',
          isLead: false,
          additionalData: { height: '' },
          phoneNumbers: [{ number: '+1 (555) 345-6789', isPrimary: true } as any],
          phoneNumber: '+1 (555) 345-6789'
        },
        viewed: true,
        flagged: false,
        startedAt: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
        endedAt: new Date(Date.now() - 86340000).toISOString(),
        recordingUrl: '',
        hasRecordingExpired: false,
        status: 'FINISHED' as any,
        bookingMessage: false,
        sentLinks: [],
        transferDestination: { department: 'Billing', number: '+1 (555) 999-0000', type: 'warm' },
        appointment: null,
        questions: []
      },
      // SMS - Another message
      {
        id: `mock-sms-2`,
        businessId: 1,
        customerId: 4,
        bookingId: null,
        transcript: 'Customer: What are your business hours?\nRosie: We are open Monday through Friday, 9 AM to 6 PM. Is there anything else I can help you with?\nCustomer: Thanks!',
        summary: 'Customer asked about business hours.',
        events: [],
        type: 'message' as any,
        entityType: EntityType.Chat,
        channel: 'sms' as any,
        customer: {
          id: 4,
          businessId: 1,
          firstName: 'Emily',
          lastName: 'Davis',
          isLead: true,
          additionalData: { height: '' },
          phoneNumbers: [{ number: '+1 (555) 456-7890', isPrimary: true } as any],
          phoneNumber: '+1 (555) 456-7890'
        },
        viewed: false,
        flagged: false,
        startedAt: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
        endedAt: new Date(Date.now() - 172700000).toISOString(),
        recordingUrl: '',
        hasRecordingExpired: false,
        status: 'FINISHED' as any,
        bookingMessage: false,
        sentLinks: [],
        transferDestination: null,
        appointment: null,
        questions: []
      }
    ];
  }
}

export enum CallStatus {
  All = 'all',
  Open = 'open',
  Starred = 'starred',
  Archived = 'archived',
  Spam = 'spam'
}

export enum ConversationType {
  CallsAndSms = 'callsAndSms',
  Calls = 'calls',
  Sms = 'sms',
  WebChat = 'webChat',
  Messenger = 'messenger',
  Instagram = 'instagram'
}

/**
 * Multi-select Source values for the inbox filter (ENG-1812/ENG-1813). These map
 * 1:1 to the API's `sources[]` allowed values. An empty selection means "All".
 */
export enum ConversationSource {
  Call = 'call',
  Sms = 'sms',
  Web = 'web',
  Messenger = 'messenger',
  Instagram = 'instagram',
  Yelp = 'yelp'
}

/** All source values, used to validate restored/persisted selections. */
export const CONVERSATION_SOURCES: ConversationSource[] = [
  ConversationSource.Call,
  ConversationSource.Sms,
  ConversationSource.Web,
  ConversationSource.Messenger,
  ConversationSource.Instagram,
  ConversationSource.Yelp,
];

export interface ConversationsResponse {
  data: Conversation[];
  meta: PaginationInfo;
}

export interface TransferDepartmentOption {
  id: string;
  department: string;
}

interface RawTransferDepartment {
  id: string | number;
  department: string;
}
